// ===========================================
// Custom Role API Functions
// ===========================================
// API calls for RBAC custom role and staff role management

import { apiClient } from '../services/api';
import { PermissionGroupDefinition } from '../types/permissions.types';

// ===========================================
// TYPES
// ===========================================

export interface RolePermission {
  id: string;
  resource: string;
  action: string;
}

export interface CustomRole {
  id: string;
  schoolId: string;
  name: string;
  description: string | null;
  color: string | null;
  isSystem: boolean;
  /** 'STAFF' for the Staff role every school has (roles slice 3); null for the school's own roles. */
  systemKey: string | null;
  createdAt: string;
  updatedAt: string;
  _count: {
    Permissions: number;
    StaffMembers: number;
  };
}

export interface CustomRoleDetail {
  id: string;
  schoolId: string;
  name: string;
  description: string | null;
  color: string | null;
  isSystem: boolean;
  systemKey: string | null;
  createdAt: string;
  updatedAt: string;
  Permissions: RolePermission[];
  StaffMembers: StaffRoleRecord[];
  /** A system role's default permissions, for "Reset to default"; null for the school's own roles. */
  defaultPermissions: { resource: string; action: string }[] | null;
}

/** A person's place in the office (roles slice 3, §7.3). */
export type OfficeAccessLevel = 'NONE' | 'STAFF' | 'ADMIN';

export interface OfficeAccessInput {
  level: OfficeAccessLevel;
  /** Required for STAFF, refused otherwise. */
  customRoleId?: string;
}

export interface OfficeAccessResult {
  level: OfficeAccessLevel;
  roleName: string | null;
  loginRole: 'ADMIN' | 'TEACHER' | 'PARENT';
  changed: boolean;
}

export interface StaffRoleUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'ADMIN' | 'TEACHER' | 'PARENT' | 'STUDENT';
  isActive: boolean;
}

export interface StaffRoleCustomRole {
  id: string;
  name: string;
  color: string | null;
}

export interface StaffRoleRecord {
  id: string;
  userId: string;
  schoolId: string;
  customRoleId: string;
  expiresAt: string | null;
  /** Null once the granter is hard-deleted (roles slice 2, #386); `grantedByName` keeps the name. */
  grantedById: string | null;
  grantedByName: string | null;
  createdAt: string;
  updatedAt: string;
  User: StaffRoleUser;
  CustomRole: StaffRoleCustomRole;
}

export interface StaffListParams {
  page?: number;
  limit?: number;
  search?: string;
  customRoleId?: string;
}

export interface StaffListResponse {
  data: StaffRoleRecord[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateCustomRoleInput {
  name: string;
  description?: string;
  color?: string;
  permissions: { resource: string; action: string }[];
}

export interface UpdateCustomRoleInput {
  name?: string;
  /** null clears it; omitted leaves it unchanged */
  description?: string | null;
  /** null clears it; omitted leaves it unchanged */
  color?: string | null;
  permissions?: { resource: string; action: string }[];
}

export interface AssignStaffRoleInput {
  userId: string;
  customRoleId: string;
  expiresAt?: string | null;
}

export interface UpdateStaffRoleInput {
  customRoleId?: string;
  expiresAt?: string | null;
}

export interface AssignableUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  /** The picker returns teacher and parent logins only since roles slice 3 (R3); an edited staff
   *  record built from an older row can still carry another role. */
  role: 'ADMIN' | 'TEACHER' | 'PARENT' | 'STUDENT';
  hasStaffRole: boolean;
}

/** An Admin of the school, with its support-account flag (roles slice 2, R13). */
export interface SchoolAdmin {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  isSupportAccount: boolean;
}

interface ApiResponse<T> {
  status: string;
  data: T;
}

// ===========================================
// CUSTOM ROLE API
// ===========================================

export const customRoleApi = {
  /**
   * List all custom roles for the school
   */
  getCustomRoles: () =>
    apiClient
      .get<ApiResponse<CustomRole[]>>('/admin/roles')
      .then((res) => res.data),

  /**
   * Get a single custom role with permissions and staff
   */
  getCustomRole: (roleId: string) =>
    apiClient
      .get<ApiResponse<CustomRoleDetail>>(`/admin/roles/${roleId}`)
      .then((res) => res.data),

  /**
   * Get the available permissions catalogue (static, grouped)
   */
  getAvailablePermissions: () =>
    apiClient
      .get<ApiResponse<PermissionGroupDefinition[]>>('/admin/roles/permissions')
      .then((res) => res.data),

  /**
   * Create a new custom role
   */
  createCustomRole: (data: CreateCustomRoleInput) =>
    apiClient
      .post<ApiResponse<CustomRoleDetail>>('/admin/roles', data)
      .then((res) => res.data),

  /**
   * Update an existing custom role
   */
  updateCustomRole: (roleId: string, data: UpdateCustomRoleInput) =>
    apiClient
      .put<ApiResponse<CustomRoleDetail>>(`/admin/roles/${roleId}`, data)
      .then((res) => res.data),

  /**
   * Delete a custom role
   */
  deleteCustomRole: (roleId: string) =>
    apiClient
      .delete<ApiResponse<{ message: string }>>(`/admin/roles/${roleId}`)
      .then((res) => res.data),

  /**
   * List staff members with their assigned roles
   */
  getStaffMembers: (params?: StaffListParams) =>
    apiClient
      .get<ApiResponse<StaffListResponse>>('/admin/roles/staff', { params })
      .then((res) => res.data),

  /**
   * Move a person between Admin, a staff role and neither, in one step (owner only)
   */
  setOfficeAccess: (userId: string, data: OfficeAccessInput) =>
    apiClient
      .put<ApiResponse<OfficeAccessResult>>(`/admin/roles/users/${userId}/office-access`, data)
      .then((res) => res.data),

  /**
   * Search for active PARENT and TEACHER logins that can be assigned a staff role (never an Admin, R3)
   */
  getAssignableUsers: (search?: string) =>
    apiClient
      .get<ApiResponse<AssignableUser[]>>('/admin/roles/staff/assignable-users', {
        params: search ? { search } : undefined,
      })
      .then((res) => res.data),

  /**
   * Assign a custom role to a user (upsert if already assigned)
   */
  assignStaffRole: (data: AssignStaffRoleInput) =>
    apiClient
      .post<ApiResponse<StaffRoleRecord>>('/admin/roles/staff', data)
      .then((res) => res.data),

  /**
   * Update an existing staff role assignment
   */
  updateStaffRole: (userId: string, data: UpdateStaffRoleInput) =>
    apiClient
      .put<ApiResponse<StaffRoleRecord>>(`/admin/roles/staff/${userId}`, data)
      .then((res) => res.data),

  /**
   * Revoke a staff role from a user
   */
  revokeStaffRole: (userId: string) =>
    apiClient
      .delete<ApiResponse<{ message: string }>>(`/admin/roles/staff/${userId}`)
      .then((res) => res.data),

  /**
   * The school's Admins, each with its support-account flag
   */
  getAdmins: () =>
    apiClient
      .get<ApiResponse<SchoolAdmin[]>>('/admin/roles/admins')
      .then((res) => res.data),

  /**
   * Mark or unmark an Admin as a support account
   */
  setSupportAccount: (userId: string, value: boolean) =>
    apiClient
      .put<ApiResponse<SchoolAdmin>>(`/admin/roles/admins/${userId}/support-account`, { value })
      .then((res) => res.data),
};
